import { useCallback, useEffect, useState } from "react";

// Owns the draggable node list: keeps a local copy of `initialNodes` (so x/y
// can move smoothly during a drag) and re-syncs it whenever the upstream
// data actually changes.
export default function useNodeDrag({ initialNodes, screenToWorld, onDragEnd }) {
    const [nodes, setNodes] = useState(initialNodes);
    const [draggingNodeId, setDraggingNodeId] = useState(null);
    const [nodeDragOffset, setNodeDragOffset] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (initialNodes && Array.isArray(initialNodes)) {
            setNodes(initialNodes);
        }
    }, [initialNodes]);

    const startNodeDrag = useCallback(
        (e, node) => {
            if (e.button !== 0) return;
            e.stopPropagation();

            const world = screenToWorld(e.clientX, e.clientY);
            setDraggingNodeId(node.id);
            setNodeDragOffset({ x: world.x - node.x, y: world.y - node.y });
        },
        [screenToWorld]
    );

    const handleDragMove = useCallback(
        (e) => {
            if (!draggingNodeId) return;

            const world = screenToWorld(e.clientX, e.clientY);
            setNodes((prev) =>
                prev.map((node) =>
                    node.id === draggingNodeId
                        ? { ...node, x: world.x - nodeDragOffset.x, y: world.y - nodeDragOffset.y }
                        : node
                )
            );
        },
        [draggingNodeId, nodeDragOffset, screenToWorld]
    );

    const handleDragEnd = useCallback(() => {
        if (draggingNodeId) {
            const updatedNode = nodes.find((n) => n.id === draggingNodeId);
            if (updatedNode) onDragEnd(updatedNode);
        }
        setDraggingNodeId(null);
    }, [draggingNodeId, nodes, onDragEnd]);

    return { nodes, draggingNodeId, startNodeDrag, handleDragMove, handleDragEnd };
}
